// Browser-wide shared auth ownership state.
//
// `localStorage.token` remains the legacy-compatible shared application IdToken
// (the recovery/shared account signal). In ADDITION, this module maintains a
// collision-resistant mutation revision so that token ABA (A → B → A, or
// null → B → null) is detectable even when a StorageEvent has not yet been
// delivered: any async operation snapshots BOTH the shared token AND the shared
// revision, and rejects if either has changed.
//
// Every AUTHORITATIVE AWS shared-token write/removal must go through
// commitSharedAuthToken()/clearSharedAuthToken(). The identity-bearing token is
// mutated FIRST and the ABA revision SECOND: an intermediate state (token
// changed/removed, revision unchanged) already invalidates the tab-applied
// bearer, so a remote tab can never re-enable a stale identity during an
// account switch. localStorage has no multi-key transaction, so ordering is the
// only reliable cross-tab synchronization.
//
// This module is deliberately lightweight (no amazon-cognito-identity-js) so the
// request layer can import it without pulling in the Cognito SDK.

export const SHARED_AUTH_TOKEN_KEY = 'token';
export const SHARED_AUTH_REVISION_KEY = 'aws_auth_revision';

export interface SharedAuthSnapshot {
  token: string | null;
  revision: string | null;
}

const newRevision = (): string => {
  const cryptoObj = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  // Browser-safe fallback: random enough to be collision-resistant for a
  // per-mutation revision that only needs to differ from the prior value.
  return `rev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

// Commit an accepted AWS IdToken as the browser-wide shared account.
// Writes the token FIRST, then the ABA revision, and returns the new revision so
// the caller can apply it atomically to the tab. The token change alone is what
// invalidates any stale tab-applied bearer during the intermediate window.
export const commitSharedAuthToken = (idToken: string): string => {
  const revision = newRevision();
  localStorage.setItem(SHARED_AUTH_TOKEN_KEY, idToken);
  localStorage.setItem(SHARED_AUTH_REVISION_KEY, revision);
  return revision;
};

// Clear the browser-wide shared account. Removes the token FIRST, then writes
// the new revision, so the intermediate (token null, revision old) state already
// fails closed for any tab still applying the removed identity.
export const clearSharedAuthToken = (): void => {
  const revision = newRevision();
  localStorage.removeItem(SHARED_AUTH_TOKEN_KEY);
  localStorage.setItem(SHARED_AUTH_REVISION_KEY, revision);
};

export const getSharedAuthToken = (): string | null =>
  localStorage.getItem(SHARED_AUTH_TOKEN_KEY);

export const getSharedAuthRevision = (): string | null =>
  localStorage.getItem(SHARED_AUTH_REVISION_KEY);

export const getSharedAuthSnapshot = (): SharedAuthSnapshot => ({
  token: getSharedAuthToken(),
  revision: getSharedAuthRevision(),
});
